import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Stack,
  Divider,
  Chip,
  Avatar,
  alpha,
  useTheme,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText
} from '@mui/material';
import Grid from '@mui/material/GridLegacy';
import {
  Close as CloseIcon,
  Person as PersonIcon,
  Email as EmailIcon,
  CalendarMonth as CalendarIcon,
  Badge as BadgeIcon,
  Male as MaleIcon,
  Female as FemaleIcon,
  WorkOutline as WorkIcon,
  PriorityHigh as PriorityIcon,
  Business as BusinessIcon,
  Language as LanguageIcon,
  Phone as PhoneIcon,
  Smartphone as SmartphoneIcon,
  Link as LinkIcon
} from '@mui/icons-material';
import { format, parseISO } from 'date-fns';
import { ja } from 'date-fns/locale';
import { motion } from 'framer-motion';
import type { Doctor, DoctorStatus, ShiftPriority } from '../../types/doctor';
import { getServiceName, getLanguageName } from '../../constants/doctorOptions';
import { formatPhoneNumber } from '../../utils/phoneFormatter';

interface DoctorDetailModalProps {
  open: boolean;
  doctor: Doctor | null;
  onClose: () => void;
  onEdit?: (doctor: Doctor) => void;
}

/**
 * 医師詳細表示モーダルコンポーネント
 * 
 * 意図：
 * - 医師の詳細情報を見やすく表示
 * - 重要な情報をセクション分けして整理
 * - アニメーションで快適なUX提供
 * 
 * 注意点：
 * - 複数の連絡先メールアドレスを全て表示
 * - 日付は日本語フォーマット
 * 
 * 改善提案：
 * - シフト履歴の表示
 * - 勤務実績の可視化
 * - 連絡先へのクイックアクション
 */
export const DoctorDetailModal: React.FC<DoctorDetailModalProps> = ({
  open,
  doctor,
  onClose,
  onEdit
}) => {
  const theme = useTheme();

  if (!doctor) return null;
  
  // デバッグログ
  console.log('🔍 DoctorDetailModal - 受け取ったdoctorデータ:', doctor);
  console.log('📱 personal_phone:', doctor.personal_phone);
  console.log('📱 personal_tel_number:', (doctor as any).personal_tel_number);
  console.log('📱 company_phone:', doctor.company_phone);
  console.log('📱 company_tel_number:', (doctor as any).company_tel_number);

  // ステータスの色設定
  const getStatusColor = (status: DoctorStatus): 'success' | 'default' => {
    switch (status) {
      case '契約済み':
        return 'success';
      case '解約済み':
        return 'default';
      default:
        return 'default';
    }
  };

  // シフト優先度の色設定（柔らかい色）
  const getPriorityChipStyle = (priority: ShiftPriority) => {
    switch (priority) {
      case 'A':
        return {
          backgroundColor: '#FCE4EC',
          color: '#C2185B'
        };
      case 'B':
        return {
          backgroundColor: '#FFF3E0',
          color: '#F57C00'
        };
      case 'C':
        return {
          backgroundColor: '#E3F2FD',
          color: '#1976D2'
        };
      default:
        return {};
    }
  };

  // 日付フォーマット
  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return '-';
    try {
      return format(parseISO(dateStr), 'yyyy年MM月dd日', { locale: ja });
    } catch {
      return dateStr;
    }
  };

  // メールアドレスの配列取得
  const getEmailList = (emails: string[] | string): string[] => {
    if (Array.isArray(emails)) {
      return emails;
    }
    return emails.split(',').map(email => email.trim()).filter(email => email);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        component: motion.div,
        initial: { opacity: 0, scale: 0.9 },
        animate: { opacity: 1, scale: 1 },
        exit: { opacity: 0, scale: 0.9 },
        sx: {
          borderRadius: 2,
          boxShadow: theme.shadows[10]
        }
      }}
    >
      <DialogTitle
        sx={{
          p: 3,
          backgroundColor: alpha(theme.palette.primary.main, 0.03),
          borderBottom: `1px solid ${theme.palette.divider}`
        }}
      >
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Stack direction="row" alignItems="center" spacing={2}>
            <Avatar
              sx={{
                width: 48,
                height: 48,
                backgroundColor: theme.palette.primary.main,
                color: 'white'
              }}
            >
              <PersonIcon />
            </Avatar>
            <Box>
              <Typography variant="h6" fontWeight={600}>
                医師詳細情報
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {doctor.doctor_code}
              </Typography>
            </Box>
          </Stack>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Stack>
      </DialogTitle>

      <DialogContent sx={{ p: 3 }}>
        <Grid container spacing={3}>
          {/* 基本情報セクション */}
          <Grid item xs={12}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              基本情報
            </Typography>
            <Box
              sx={{
                p: 2,
                borderRadius: 1,
                backgroundColor: alpha(theme.palette.grey[100], 0.5),
                border: `1px solid ${theme.palette.divider}`
              }}
            >
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <BadgeIcon fontSize="small" color="action" />
                    <Typography variant="body2" color="text.secondary">
                      医師コード
                    </Typography>
                  </Stack>
                  <Typography variant="body1" fontWeight={500} sx={{ mt: 0.5 }}>
                    {doctor.doctor_code}
                  </Typography>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <PersonIcon fontSize="small" color="action" />
                    <Typography variant="body2" color="text.secondary">
                      氏名
                    </Typography>
                  </Stack>
                  <Typography variant="body1" fontWeight={500} sx={{ mt: 0.5 }}>
                    {doctor.first_name}{doctor.last_name}先生
                  </Typography>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    {doctor.gender === 'male' ? (
                      <MaleIcon fontSize="small" color="action" />
                    ) : (
                      <FemaleIcon fontSize="small" color="action" />
                    )}
                    <Typography variant="body2" color="text.secondary">
                      性別
                    </Typography>
                  </Stack>
                  <Typography variant="body1" fontWeight={500} sx={{ mt: 0.5 }}>
                    {doctor.gender === 'male' ? '男性' : '女性'}
                  </Typography>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <WorkIcon fontSize="small" color="action" />
                    <Typography variant="body2" color="text.secondary">
                      稼働ステータス
                    </Typography>
                  </Stack>
                  <Box sx={{ mt: 0.5 }}>
                    <Chip
                      label={doctor.status}
                      size="small"
                      color={getStatusColor(doctor.status)}
                    />
                  </Box>
                </Grid>
              </Grid>
            </Box>
          </Grid>

          <Grid item xs={12}>
            <Divider />
          </Grid>

          {/* 勤務情報セクション */}
          <Grid item xs={12}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              勤務情報
            </Typography>
            <Box
              sx={{
                p: 2,
                borderRadius: 1,
                backgroundColor: alpha(theme.palette.grey[100], 0.5),
                border: `1px solid ${theme.palette.divider}`
              }}
            >
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <CalendarIcon fontSize="small" color="action" />
                    <Typography variant="body2" color="text.secondary">
                      稼働開始日
                    </Typography>
                  </Stack>
                  <Typography variant="body1" fontWeight={500} sx={{ mt: 0.5 }}>
                    {formatDate(doctor.start_date)}
                  </Typography>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <CalendarIcon fontSize="small" color="action" />
                    <Typography variant="body2" color="text.secondary">
                      稼働解約日
                    </Typography>
                  </Stack>
                  <Typography variant="body1" fontWeight={500} sx={{ mt: 0.5 }}>
                    {formatDate(doctor.termination_date)}
                  </Typography>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <PriorityIcon fontSize="small" color="action" />
                    <Typography variant="body2" color="text.secondary">
                      シフト優先度
                    </Typography>
                  </Stack>
                  <Box sx={{ mt: 0.5 }}>
                    <Chip
                      label={`優先度 ${doctor.shift_priority}`}
                      size="small"
                      sx={getPriorityChipStyle(doctor.shift_priority)}
                    />
                  </Box>
                </Grid>

                {/* 対応可能業務 */}
                <Grid item xs={12}>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <BusinessIcon fontSize="small" color="action" />
                    <Typography variant="body2" color="text.secondary">
                      対応可能業務
                    </Typography>
                  </Stack>
                  <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {doctor.supported_services && doctor.supported_services.length > 0 ? (
                      doctor.supported_services.map((service, index) => (
                        <Chip
                          key={index}
                          label={getServiceName(service)}
                          size="small"
                          sx={{
                            backgroundColor: '#E8F5E9',
                            color: '#2E7D32'
                          }}
                        />
                      ))
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        未設定
                      </Typography>
                    )}
                  </Box>
                </Grid>

                {/* 対応可能言語 */}
                <Grid item xs={12}>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <LanguageIcon fontSize="small" color="action" />
                    <Typography variant="body2" color="text.secondary">
                      対応可能言語
                    </Typography>
                  </Stack>
                  <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {doctor.supported_languages && doctor.supported_languages.length > 0 ? (
                      doctor.supported_languages.map((language, index) => (
                        <Chip
                          key={index}
                          label={getLanguageName(language)}
                          size="small"
                          sx={{
                            backgroundColor: '#E3F2FD',
                            color: '#1565C0'
                          }}
                        />
                      ))
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        未設定
                      </Typography>
                    )}
                  </Box>
                </Grid>
              </Grid>
            </Box>
          </Grid>

          <Grid item xs={12}>
            <Divider />
          </Grid>

          {/* 連絡先情報セクション */}
          <Grid item xs={12}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              連絡先情報
            </Typography>
            <Box
              sx={{
                p: 2,
                borderRadius: 1,
                backgroundColor: alpha(theme.palette.grey[100], 0.5),
                border: `1px solid ${theme.palette.divider}`
              }}
            >
              <Grid container spacing={2}>
                {/* 診療リストスプレッドシート */}
                <Grid item xs={12}>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <LinkIcon fontSize="small" color="action" />
                    <Typography variant="body2" color="text.secondary">
                      診療リストスプレッドシート
                    </Typography>
                  </Stack>
                  <Typography variant="body1" fontWeight={500} sx={{ mt: 0.5 }}>
                    {doctor.medical_spreadsheet_url ? (
                      <a
                        href={doctor.medical_spreadsheet_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ color: theme.palette.primary.main }}
                      >
                        スプレッドシートを開く
                      </a>
                    ) : (
                      '未設定'
                    )}
                  </Typography>
                </Grid>

                {/* メールアドレス */}
                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    メールアドレス
                  </Typography>
                  <List dense disablePadding>
                    {getEmailList(doctor.contact_emails).map((email, index) => (
                      <ListItem key={index} disableGutters>
                        <ListItemIcon sx={{ minWidth: 32 }}>
                          <EmailIcon fontSize="small" color="action" />
                        </ListItemIcon>
                        <ListItemText
                          primary={email}
                          primaryTypographyProps={{
                            variant: 'body2',
                            sx: { wordBreak: 'break-all' }
                          }}
                        />
                      </ListItem>
                    ))}
                  </List>
                </Grid>

                {/* 個人用携帯番号 */}
                <Grid item xs={12} sm={6}>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <SmartphoneIcon fontSize="small" color="action" />
                    <Typography variant="body2" color="text.secondary">
                      個人用携帯番号
                    </Typography>
                  </Stack>
                  <Typography variant="body1" fontWeight={500} sx={{ mt: 0.5 }}>
                    {(doctor.personal_phone || doctor.personal_tel_number) ? formatPhoneNumber(doctor.personal_phone || doctor.personal_tel_number) : '未設定'}
                  </Typography>
                </Grid>

                {/* 社用携帯番号 */}
                <Grid item xs={12} sm={6}>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <PhoneIcon fontSize="small" color="action" />
                    <Typography variant="body2" color="text.secondary">
                      社用携帯番号
                    </Typography>
                  </Stack>
                  <Typography variant="body1" fontWeight={500} sx={{ mt: 0.5 }}>
                    {(doctor.company_phone || doctor.company_tel_number) ? formatPhoneNumber(doctor.company_phone || doctor.company_tel_number) : '未設定'}
                  </Typography>
                </Grid>
              </Grid>
            </Box>
          </Grid>

          {/* タイムスタンプ */}
          {(doctor.created_at || doctor.updated_at) && (
            <Grid item xs={12}>
              <Box sx={{ mt: 1 }}>
                {doctor.created_at && (
                  <Typography variant="caption" color="text.secondary" display="block">
                    作成日時: {format(parseISO(doctor.created_at), 'yyyy年MM月dd日 HH:mm', { locale: ja })}
                  </Typography>
                )}
                {doctor.updated_at && (
                  <Typography variant="caption" color="text.secondary" display="block">
                    更新日時: {format(parseISO(doctor.updated_at), 'yyyy年MM月dd日 HH:mm', { locale: ja })}
                  </Typography>
                )}
              </Box>
            </Grid>
          )}
        </Grid>
      </DialogContent>

      <DialogActions sx={{ p: 2.5, borderTop: `1px solid ${theme.palette.divider}` }}>
        <Button onClick={onClose} color="inherit">
          閉じる
        </Button>
        {onEdit && (
          <Button
            onClick={() => onEdit(doctor)}
            variant="contained"
            startIcon={<PersonIcon />}
          >
            編集する
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};
